"use client";

import { forwardRef } from "react";
import PropTypes from "prop-types";
import { grey3, grey90, spacing32, white, whiteTransparency10, whiteTransparency20, whiteTransparency60, setAppearanceConstantLocal, textColorInverted } from "@churchofjesuschrist/eden-style-constants";
import styled from "styled-components";
import shared from "./shared";
import { jsx as _jsx } from "react/jsx-runtime";
const iconSpecificFontSize = "1.5rem";
const [buttonIconHoverDef, buttonIconHover] = setAppearanceConstantLocal("button-icon-hover", {
  light: grey3,
  dark: whiteTransparency60,
  prefix: "eden"
});
const [buttonIconActiveBackgroundDef, buttonIconActiveBackground] = setAppearanceConstantLocal("button-icon-active-background", {
  light: grey90,
  dark: whiteTransparency10,
  prefix: "eden"
});
const Component = styled.button.withConfig({
  componentId: "sc-1g7hsbc-0"
})(["", ";", ";", ";border:none;position:relative;&:before{content:\"\";display:block;width:100%;height:100%;position:absolute;}border-radius:50%;", ";font-size:", ";padding:0;background:transparent;color:currentcolor;@media (any-hover:hover){&:hover{background:", ";color:currentcolor;}}&:active,&[aria-pressed=\"true\"]{background:", ";color:", ";}&[disabled],&[disabled]:hover{background:transparent;}&:is(a):visited:not(:active){color:currentcolor;}", ";"], shared, buttonIconHoverDef, buttonIconActiveBackgroundDef, ({
  size
}) => size === "large" ? `min-width: 3.5rem;
            min-height: 3.5rem;` : size === "small" ? `min-width: ${spacing32};
              min-height: ${spacing32};` : `min-width: 2.5rem;
        min-height: 2.5rem;`, iconSpecificFontSize, buttonIconHover, buttonIconActiveBackground, textColorInverted, ({
  $overImage
}) => $overImage ? `
     border: 1px solid ${white};
     background: ${whiteTransparency20};
     color: ${grey90};

     &:hover {
      background: ${whiteTransparency10};
      border: none;
      color: ${grey90};
     }

     &:active,
     &[aria-pressed="true"] {
       border: 1px solid ${whiteTransparency20};
       background: #00000099;
       color: ${whiteTransparency10};
     }
  ` : "");
/**
 * `Icon` buttons are used when only an icon, without visual text, should be clickable.
 */
const Icon = /*#__PURE__*/forwardRef(({
  size = "normal",
  href,
  disabled,
  renderAs = disabled ? "button" : href ? "a" : "button",
  type = renderAs === "button" ? "button" : undefined,
  overImage,
  ...props
}, ref) => /*#__PURE__*/_jsx(Component, {
  ...props,
  size: size,
  ref: ref,
  type: type,
  href: href,
  disabled: disabled,
  as: renderAs,
  $overImage: overImage,
  children: props.children
}));
Icon.displayName = "Icon";
Icon.propTypes = {
  /** The icon */
  children: PropTypes.node.isRequired,
  /** When present, the button becomes a "toggle" button. The value indicates whether it is toggled on or not. */
  "aria-pressed": PropTypes.oneOf([true, false]),
  /** Defines a value that labels the button. If another element is visible in your UI that already labels the
   * purpose of the button, use the 'aria-labelledby' prop instead. This prop is required if `aria-labelledby` is not used. */
  "aria-label": PropTypes.string,
  /** Identifies the element(s) that label(s) the button. Must be a space-separated list of one or more `id` values referencing
   * the element(s) present in the UI that label(s) the button. If no accompanying visible label(s) exist, you must instead use
   * the 'aria-label' prop instead. */
  "aria-labelledby": PropTypes.string,
  /** Whether to apply styles that help the button stand out on an image background. */
  overImage: PropTypes.bool,
  /** The size of the button */
  size: PropTypes.oneOf(["small", "normal", "large"]),
  /** The [type of the button](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#Attributes) or the [type of the link](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#Attributes). */
  type: PropTypes.string,
  /** If the button should be disabled */
  disabled: PropTypes.bool,
  /** The link that the button should navigate to on click */
  href: PropTypes.any,
  /** The HTML tag string or React component to render as.
   *
   * By default, if `href` is present and it is not `disabled`, it will render as an `<a>`. Otherwise it will default to `<button>`. */
  renderAs: PropTypes.elementType
};
export default Icon;